import {cs}             from "@Common/react/chain-services";
import {consumeContext} from "@Common/react/context";
import {Load2}          from "@Common/react/load2";
import {UseState}       from "@Common/react/use-state";
import {isDevQuan}      from "@Common/ui-components/debug/is-dev-quan";
import {AlertModal}    from "@Common/ui-components/heavy/common/design2/dialogs/alert-modal/alert-modal";
import {getNestedValue} from "@Common/utils/arr-path";
import {sum}            from "@Common/utils/collections";
import {isSuperSmall}   from "@Common/utils/math/is-super-small";
import {isApiTrue}  from "../../is-api-true";
import {BufferForm} from "../common/buffer-form";

import {createNonceCard}  from "./create-nonce-card";
import {getSelectedCards} from "./get-selected-cards";
import {setCartPayments}  from "./set-cart-payments";
import {setCreditCards}   from "./set-credit-cards";
import {setNonceCard}     from "./set-nonce-card";

export const CreditCardsLogic = ({store, remainingAmount, next}) => cs(
    consumeContext("apis"),

    consumeContext("company"),

    ["cards", ({apis, company}, next) => Load2({
        _key: JSON.stringify({company: !!company}),
        fetch: () => isApiTrue(company?.prefs?.hideCreditCardCollection) ? [] : apis.next.creditCard.getCreditCards(),
        next,
    })],
    ["selectedCards", ({cards}, next) => next((!store.cart || !cards.value) ? [] : getSelectedCards({
        cart: store.cart,
        cards: cards.value,
        remainingAmount,
    }))],
    ["selectMultiple", (_, next) => UseState({
        initValue: !!isDevQuan(),
        next,
    })],

    ["multiple", ({selectMultiple, selectedCards}, next) => next(selectMultiple.value || selectedCards?.length > 1)],

    ["assumedAmount", ({selectedCards, multiple}, next) => next(getAssumedAmount({
        total: remainingAmount,
        cards: selectedCards,
        multiple
    }))],

    ["appliedAmountCards", ({assumedAmount, selectedCards}, next) => next(!assumedAmount ? selectedCards : (
        selectedCards.map((c) => c.cardToken != assumedAmount.cardToken ? c : {
            ...c,
            amount: assumedAmount.amount,
        })
    ))],
    ["errors", ({appliedAmountCards, multiple, assumedAmount}, next) => next([
        isNaN(remainingAmount) && (
            "Invalid amount: " + remainingAmount
        ),
        !isNaN(remainingAmount) && (!isSuperSmall(remainingAmount) && !appliedAmountCards?.length) && (
            "Please select an additional payment method to cover the remaining balance"
        ),
        (
            store.cart.items.some((item) => item.approvalInfo?.approvalRequired) &&
            store.cart.appliedPayments?.creditCards?.some((card) => card.type === "nonce")
        ) && (
            "Unsaved cards cannot be used for approval orders. Please select another form of payment."
        ),
        ...multiple && [
            !(appliedAmountCards?.length > 1) ? (
                "Please select multiple cards"
            ) : (() => {
                if (appliedAmountCards.some((c) => !c.amount)) {
                    return "Please select amount for each card";
                }
                const sumAmount = sum(appliedAmountCards, (c) => c.amount);
                if (sumAmount > remainingAmount + .01) {
                    return "Amounts entered larger than cart balance total. Please provide amounts to charge that equal cart balance and try again.";
                }
                if (sumAmount < remainingAmount - .01) {
                    return "Amounts entered do not equal the cart balance total. Please provide amounts to charge that equal cart balance and try again.";
                }
                return null;
            })(),
        ].filter(v => v) || [],
    ].filter(v => v))],
    ["alertModal", ({}, next) => AlertModal({next})],
    ["selectedCardsB", ({apis, selectedCards}, next) => BufferForm({
        value: selectedCards,
        disableBuffer: !store.saving,
        applyChange: (value) => store.dispatch(setCreditCards(value, {
            apis,
            clearBilling: isSuperSmall(remainingAmount) && !selectedCards?.length,
        })),
        next,
    })],

    ({apis, selectedCards, selectedCardsB, errors, multiple, appliedAmountCards, cards, alertModal, selectMultiple, assumedAmount}) => next({

        beforeCheckout: async () => await setCartPayments(store.cart, {
            cards: appliedAmountCards,
            apis,
        }),
        errors,
        summaryCards: appliedAmountCards?.map((c) => ({
            amount: c.amount,
            cardLast4: c.cardLast4 ?? cards.value.find((c1) => c1.cardToken === c.cardToken)?.cardLast4,
        })),
        selectedCards: selectedCardsB.value,

        cards: cards.value,

        multiple: {
            value: multiple,
            onToggle: async () => {
                if (multiple) {

                    selectMultiple.onChange(false);

                    if (selectedCards?.length > 0) {
                        await store.dispatch(setCreditCards([{
                            ...selectedCards[0],
                            amount: null
                        }], {apis}));
                    }
                } else {
                    const cardCount = cards.value?.filter((c) => !c.isExpiredCard)?.length + (selectedCards?.some(card => card.type === "nonce") ? 1 : 0);
                    if (cardCount === 0) {
                        alertModal.show({
                            message: "No card available. Please add a card and try again.",
                        })
                    } else if (cardCount === 1) {
                        alertModal.show({
                            message: "Only one card is available. Please add a second card and try again.",
                        })
                    } else {
                        selectMultiple.onChange(true);
                    }
                }
            },
        },
        getAssumedAmount: (card) => !assumedAmount || assumedAmount.cardToken !== card.cardToken ? null : assumedAmount.amount,
        onSaveCardAmount: async (amount, card) => {
            await store.dispatch(setCreditCards(
                selectedCards.map((c) => c.cardToken !== card.cardToken ? c : {...c, amount}),
                {apis}
            ));
        },
        updateNonceCard: async ({card, isDefaultCard, saveToProfile, btTokenResult}) => {
            if (saveToProfile) {
                await apis.next.creditCard.addUpdate({
                    ...card,
                    cardToken: null,
                    "braintreeNonce" : btTokenResult.nonce,
                    "makeDefault" : isDefaultCard,
                });

                await Promise.all([
                    cards.reload(),
                    isDefaultCard && getNestedValue(store.cart, ["appliedPayments", "creditCards", 0]) != null && (
                        store.dispatch(setCreditCards(null, {apis}))
                    ),
                ]);
            } else {
                await store.dispatch(setNonceCard(createNonceCard(card, btTokenResult), {apis}));
            }
        },
        updateSavedCard: async ({card, isDefaultCard, saveToProfile, btTokenResult}) => {
            if (saveToProfile) {
                await apis.next.creditCard.addUpdate({
                    ...card,
                    "braintreeNonce" : btTokenResult.nonce,
                    "makeDefault" : isDefaultCard,
                });

                await Promise.all([
                    cards.reload(),
                    store.dispatch(setCreditCards(isDefaultCard ? null : [card], {apis})),
                ]);
            } else {
                await apis.next.creditCard.deleteCard(card.cardToken);

                await Promise.all([
                    cards.reload(),
                    store.dispatch(setNonceCard(createNonceCard({...card, cardToken: null}, btTokenResult), {apis})),
                ]);
            }
        },
        addNewCard: async ({card, isDefaultCard, saveToProfile, btTokenResult}) => {
            if (saveToProfile) {
                try {
                    await apis.next.creditCard.addUpdate({
                        ...card,
                        "braintreeNonce" : btTokenResult.nonce,
                        "makeDefault" : isDefaultCard,
                    });

                    const oldCards = cards.value;
                    const newCards = await cards.reload();
                    const newCard = newCards.find((c) => !oldCards.some((c1) => c1.cardToken === c.cardToken));
                    await store.dispatch(setCreditCards(isDefaultCard ? null : [newCard], {apis}));
                } catch (e) {
                    alertModal.show({
                        message: "Failed to add new card.",
                        description: e,
                    })
                }
            } else {
                await store.dispatch(setNonceCard(createNonceCard(card, btTokenResult), {apis}));
            }
        },

        deleteSavedCard: async (card) => {
            if (selectedCards?.find((c) => c.cardToken === card.cardToken)) {
                await store.dispatch(setCreditCards(selectedCards.filter((c) => c.cardToken !== card.cardToken), {apis}));
            }

            cards.onChange(cards.value.filter((c) => c.cardToken !== card.cardToken));
            await apis.next.creditCard.deleteCard(card.cardToken);
        },
        deleteNonceCard: () => selectedCardsB.onChange(selectedCardsB.value.filter((c) => c.type !== "nonce")),
        deselectSavedCard: (card) => {
            if (!multiple) {
                selectedCardsB.onChange(null);
            } else {
                selectedCardsB.onChange(
                    selectedCardsB.value.filter((c) => c.cardToken !== card.cardToken)
                );
            }
        },
        selectSavedCard: (card) => {
            if (!multiple) {
                selectedCardsB.onChange([card]);
            } else {
                selectedCardsB.onChange(
                    [...selectedCardsB.value ?? [], card]
                );
            }
        },
    }),
);

const getAssumedAmount = ({total, cards, multiple}) => {
    if (!cards?.length) {
        return null;
    }

    const assumedCards = cards.filter((c) => c.amount == null || !multiple);
    if (assumedCards.length !== 1) {
        return null;
    }

    const totalCardAmount = sum(cards, (c) => c.amount ?? 0) - (assumedCards[0].amount ?? 0);
    return {
        cardToken: assumedCards[0].cardToken,
        amount: Math.max(0, total - totalCardAmount),
    };
};
